/**
 * DataFetchContext
 *
 * Provides data fetching operations for all views
 * Centralizes all aMule data fetching logic
 */

import React, { createContext, useContext, useCallback, useMemo, useRef, useEffect } from 'https://esm.sh/react@18.2.0';
import { useWebSocketConnection } from './WebSocketContext.js';
import { useStaticData } from './StaticDataContext.js';
import { useLiveData } from './LiveDataContext.js';

const { createElement: h } = React;

const DataFetchContext = createContext(null);

// History refresh interval (5 seconds)
const HISTORY_REFRESH_INTERVAL = 5000;

/**
 * DataFetchProvider - provides data fetching functions through context
 * @param {Object} props
 * @param {ReactNode} props.children - Child components
 */
export const DataFetchProvider = ({ children }) => {
  const { sendMessage } = useWebSocketConnection();
  const { resetDataLoaded: resetStaticDataLoaded, setHistoryTrackUsername } = useStaticData();
  const {
    setDataHistory,
    setHistoryLoading,
    markDataLoaded: markLiveDataLoaded,
    dataLoaded: liveDataLoaded
  } = useLiveData();

  // Track if history auto-refresh is active
  const historyRefreshRef = useRef(null);

  // Fetch all history data from API
  const fetchHistory = useCallback(async (showLoading = true) => {
    try {
      if (showLoading) setHistoryLoading(true);
      const response = await fetch('/api/history/all');
      if (!response.ok) throw new Error('Failed to fetch history');
      const data = await response.json();
      setDataHistory(data.entries || []);
      setHistoryTrackUsername(data.trackUsername || false);
    } catch (err) {
      console.error('Error fetching history:', err);
    } finally {
      markLiveDataLoaded('history');
      if (showLoading) setHistoryLoading(false);
    }
  }, [setDataHistory, setHistoryLoading, setHistoryTrackUsername, markLiveDataLoaded]);

  // Start history auto-refresh (called when HistoryView mounts)
  const startHistoryRefresh = useCallback(() => {
    // Initial fetch
    fetchHistory(true);
    // Set up interval for subsequent refreshes (no loading indicator)
    if (historyRefreshRef.current) clearInterval(historyRefreshRef.current);
    historyRefreshRef.current = setInterval(() => fetchHistory(false), HISTORY_REFRESH_INTERVAL);
  }, [fetchHistory]);

  // Stop history auto-refresh (called when HistoryView unmounts)
  const stopHistoryRefresh = useCallback(() => {
    if (historyRefreshRef.current) {
      clearInterval(historyRefreshRef.current);
      historyRefreshRef.current = null;
    }
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (historyRefreshRef.current) clearInterval(historyRefreshRef.current);
    };
  }, []);

  const fetchPreviousSearchResults = useCallback(async (instanceId) => {
    sendMessage({ action: 'getPreviousSearchResults', ...(instanceId && { instanceId }) });
  }, [sendMessage]);

  const refreshSharedFiles = useCallback(async () => {
    sendMessage({ action: 'refreshSharedFiles' });
  }, [sendMessage]);

  const fetchLogs = useCallback((instanceId) => {
    resetStaticDataLoaded('logs');
    sendMessage({ action: 'getLog', ...(instanceId && { instanceId }) });
  }, [sendMessage, resetStaticDataLoaded]);

  const fetchServerInfo = useCallback((instanceId) => {
    resetStaticDataLoaded('serverInfo');
    sendMessage({ action: 'getServerInfo', ...(instanceId && { instanceId }) });
  }, [sendMessage, resetStaticDataLoaded]);

  const fetchAppLogs = useCallback((opts = {}) => {
    resetStaticDataLoaded('appLogs');
    // minLevel='warn' or 'error' lets the server walk the dedicated ERROR/WARN
    // ring instead of the main one — gives us up to 500 preserved warnings
    // even after a flood of DEBUG/INFO traffic. Default limit is generous
    // enough to deliver the full main ring when minLevel is debug/info.
    const payload = { action: 'getAppLog', limit: 2000 };
    if (opts.minLevel) payload.minLevel = opts.minLevel;
    sendMessage(payload);
  }, [sendMessage, resetStaticDataLoaded]);

  const fetchQbittorrentLogs = useCallback((instanceId) => {
    resetStaticDataLoaded('qbittorrentLogs');
    sendMessage({ action: 'getQbittorrentLog', ...(instanceId && { instanceId }) });
  }, [sendMessage, resetStaticDataLoaded]);

  const fetchStatsTree = useCallback((instanceId) => {
    sendMessage({ action: 'getStatsTree', ...(instanceId && { instanceId }) });
  }, [sendMessage]);

  const fetchServers = useCallback((instanceId) => {
    resetStaticDataLoaded('servers');
    sendMessage({ action: 'getServersList', ...(instanceId && { instanceId }) });
  }, [sendMessage, resetStaticDataLoaded]);

  const fetchCategories = useCallback(() => {
    resetStaticDataLoaded('categories');
    sendMessage({ action: 'getCategories' });
  }, [sendMessage, resetStaticDataLoaded]);

  // Memoize context value to prevent unnecessary re-renders of consumers
  const value = useMemo(() => ({
    fetchPreviousSearchResults,
    refreshSharedFiles,
    fetchLogs,
    fetchServerInfo,
    fetchAppLogs,
    fetchQbittorrentLogs,
    fetchStatsTree,
    fetchServers,
    fetchCategories,
    fetchHistory,
    startHistoryRefresh,
    stopHistoryRefresh
  }), [
    fetchPreviousSearchResults, refreshSharedFiles,
    fetchLogs, fetchServerInfo, fetchAppLogs, fetchQbittorrentLogs, fetchStatsTree,
    fetchServers, fetchCategories, fetchHistory, startHistoryRefresh, stopHistoryRefresh
  ]);

  return h(DataFetchContext.Provider, { value }, children);
};

export const useDataFetch = () => {
  const context = useContext(DataFetchContext);
  if (!context) {
    throw new Error('useDataFetch must be used within DataFetchProvider');
  }
  return context;
};
